<template>
  <div :class="{ 'vsm-standalone': standalone }" style="margin:20px">
    <FiPageRail v-if="!standalone" class="vsm-rail" :title="isMobile() ? 'VSM' : 'Vehicle Status Manager'" :class="{ 'vsm-upcoming-rail--mobile': isMobile() }">
      <template #actions>
        <b-button
          v-if="!cardView"
          size="sm"
          class="vst-auto-btn"
          :class="{ 'vst-auto-btn--on': auto_refresh }"
          variant="outline-secondary"
          :title="autoRefreshTitle"
          :aria-pressed="auto_refresh ? 'true' : 'false'"
          aria-label="Toggle auto-refresh"
          @click="toggleAutoRefresh"
        ><span class="vst-auto-dot" :class="{ 'vst-auto-dot--live': auto_refresh }" aria-hidden="true"></span>Auto</b-button>
        <b-button v-if="!cardView" size="sm" variant="primary" title="Download CSV" aria-label="download" @click="downloadCsv()"><i class="fa fa-download"></i></b-button>
        <vsm-print-button @click="printPage()" />
        <b-button variant="success" size="sm" @click="view_more_info = !view_more_info" aria-label="more info">
          <i class="fa fa-info-circle"></i>
        </b-button>
      </template>
    </FiPageRail>
    <div v-if="standalone">
      <span class="vsm-standalone-header" style="font-size: 20px;">
        <a target="_new" href="https://www.initekconsulting.com/fleet-integrations"><img style="margin-top:-5px" width="40" src="favicon.ico"/></a>&nbsp;Vehicle Status Manager&nbsp;
        <span v-if="selected_base">&nbsp;-&nbsp;{{selected_base}}</span>
        <span v-if="return_mode">&nbsp;- Wash Mode</span>
        <span v-if="driver_mode">&nbsp;-&nbsp;Driver</span>
        <span v-if="cleaning_mode">&nbsp;-&nbsp;Cleaning</span>
        <span v-if="maintenance_mode">&nbsp;-&nbsp;Maintenance</span>
      <b-spinner v-if="reloading" style="width: 1.8rem; height: 1.8rem"></b-spinner><span v-else style="width: 1.8rem; height: 1.8rem">&nbsp;</span>
      &nbsp;<vsm-reload-button @click="load_data()" />
      &nbsp;<vsm-print-button @click="printPage()" />
      </span>
    </div>

    <b-modal title="Here are details on the statuses and filters used for the VSM" size="xl" v-model="view_more_info" ok-only scrollable dialog-class="vsm-info-dialog" body-class="vsm-info-body">
      <h4>Vehicle Status</h4>

      <span v-if="washed_enabled">
        <div class="vsm-info-line"><b>READY</b> &mdash; automatically set by the system when a vehicle has been cleaned and washed and is ready for service</div>
        <div class="vsm-info-line"><b>CLEANED</b> &mdash; indicates that the vehicle interior has been cleaned (manually managed)</div>
        <div class="vsm-info-line"><b>WASHED</b> &mdash; indicates that the vehicle exterior has been washed (manually managed)</div>
      </span>
      <span v-else>
        <div class="vsm-info-line"><b>CLEANED</b> &mdash; indicates that the vehicle has been cleaned (manually managed)</div>
      </span>
      <div class="vsm-info-line"><b>DIRTY</b> &mdash; automatically set by the system when a vehicle arrives back to the finish location or yard</div>
      <div class="vsm-info-line"><b>OUT</b> &mdash; automatically set by the system when a vehicle leaves the start location for a planned booking</div>
      <div class="vsm-info-line"><b>OOS</b> &mdash; out of service (manually managed)</div>
      <hr/>
      <h4>VSM Standalone Mode</h4>
      <div class="vsm-info-line">provides a standalone view that refreshes every minute to keep all teams in sync (no login required)</div>
      <div class="vsm-info-line"><a target="_blank" :href="'./#/vsm-tbn/reload/' + standaloneId">click here to load the standalone Dispatch VSM (by depart time)</a><vsm-standalone-url-copy-button :href="'./#/vsm-tbn/reload/' + standaloneId" :tenant-id="tenant_id"/></div>
      <div class="vsm-info-line"><a target="_blank" :href="'./#/vsm-tbn/reloadreturn/' + standaloneId">click here to load the standalone Wash VSM (by return time)</a><vsm-standalone-url-copy-button :href="'./#/vsm-tbn/reloadreturn/' + standaloneId" :tenant-id="tenant_id"/></div>
      <div v-if="summary && summary.visible_driver_fields" class="vsm-info-line"><a target="_blank" :href="'./#/vsm-tbn/driver/' + standaloneId">click here to load the standalone Driver VSM</a><vsm-standalone-url-copy-button :href="'./#/vsm-tbn/driver/' + standaloneId" :tenant-id="tenant_id"/></div>
      <div v-if="summary && summary.visible_cleaning_fields" class="vsm-info-line"><a target="_blank" :href="'./#/vsm-tbn/cleaning/' + standaloneId">click here to load the standalone Cleaning VSM</a><vsm-standalone-url-copy-button :href="'./#/vsm-tbn/cleaning/' + standaloneId" :tenant-id="tenant_id"/></div>
      <div v-if="summary && summary.visible_maintenance_fields" class="vsm-info-line"><a target="_blank" :href="'./#/vsm-tbn/maintenance/' + standaloneId">click here to load the standalone Maintenance VSM</a><vsm-standalone-url-copy-button :href="'./#/vsm-tbn/maintenance/' + standaloneId" :tenant-id="tenant_id"/></div>
      <span v-if="base_options.length > 2">
        <div class="vsm-info-line">Click on a base to load the Dispatch VSM for that base.</div>
        <div class="vsm-info-line">
          <span v-for="base in base_options.filter(b => b.value != '')" :key="'dispatch-' + base.value">
            <a target="_blank" :href="'./#/vsm-tbn/reload/' + standaloneId + '/' + base.value">{{base.value}}</a><vsm-standalone-url-copy-button :href="'./#/vsm-tbn/reload/' + standaloneId + '/' + base.value" :tenant-id="tenant_id"/>&nbsp;&nbsp;|
          </span>
        </div>
        <div class="vsm-info-line">Click on a base to load the Wash VSM (by return time) for that base.</div>
        <div class="vsm-info-line">
          <span v-for="base in base_options.filter(b => b.value != '')" :key="'wash-' + base.value">
            <a target="_blank" :href="'./#/vsm-tbn/reloadreturn/' + standaloneId + '/' + base.value">{{base.value}}</a><vsm-standalone-url-copy-button :href="'./#/vsm-tbn/reloadreturn/' + standaloneId + '/' + base.value" :tenant-id="tenant_id"/>&nbsp;&nbsp;|
          </span>
        </div>
        <span v-if="summary && summary.visible_driver_fields">
          <div class="vsm-info-line">Click on a base to load the Driver VSM for that base.</div>
          <div class="vsm-info-line">
            <span v-for="base in base_options.filter(b => b.value != '')" :key="'driver-' + base.value">
              <a target="_blank" :href="'./#/vsm-tbn/driver/' + standaloneId + '/' + base.value">{{base.value}}</a><vsm-standalone-url-copy-button :href="'./#/vsm-tbn/driver/' + standaloneId + '/' + base.value" :tenant-id="tenant_id"/>&nbsp;&nbsp;|
            </span>
          </div>
        </span>
        <span v-if="summary && summary.visible_cleaning_fields">
          <div class="vsm-info-line">Click on a base to load the Cleaning VSM for that base.</div>
          <div class="vsm-info-line">
            <span v-for="base in base_options.filter(b => b.value != '')" :key="'cleaning-' + base.value">
              <a target="_blank" :href="'./#/vsm-tbn/cleaning/' + standaloneId + '/' + base.value">{{base.value}}</a><vsm-standalone-url-copy-button :href="'./#/vsm-tbn/cleaning/' + standaloneId + '/' + base.value" :tenant-id="tenant_id"/>&nbsp;&nbsp;|
            </span>
          </div>
        </span>
        <span v-if="summary && summary.visible_maintenance_fields">
          <div class="vsm-info-line">Click on a base to load the Maintenance VSM for that base.</div>
          <div class="vsm-info-line">
            <span v-for="base in base_options.filter(b => b.value != '')" :key="'maintenance-' + base.value">
              <a target="_blank" :href="'./#/vsm-tbn/maintenance/' + standaloneId + '/' + base.value">{{base.value}}</a><vsm-standalone-url-copy-button :href="'./#/vsm-tbn/maintenance/' + standaloneId + '/' + base.value" :tenant-id="tenant_id"/>&nbsp;&nbsp;|
            </span>
          </div>
        </span>
      </span>
      <hr/>
      <span v-if="summary && summary.aws_phone_number">
        <h4>Update Status & Notes Over SMS</h4>
        <div class="vsm-info-line">simply send a text in one of the following formats to #{{summary.aws_phone_number | prettyPhoneNumber}}</div>
        <div class="vsm-info-sub">to update the status only, send '[status] [vehicle name]' - for example 'cleaned 123'</div>
        <div class="vsm-info-sub">to update the notes only, send 'notes [vehicle name], [notes]' - for example 'notes 123, issue found'</div>
        <div class="vsm-info-sub">to update the status & notes, send '[status] [vehicle name], [notes]' - for example 'cleaned 123, issue found'</div>
        <div class="vsm-info-sub">to clear notes, send '[notes] [vehicle name]' - for example 'notes 123'</div>
        <h4>Update Numeric Fields Over SMS</h4>
        <div class="vsm-info-line">send a text in one of the following formats to #{{summary.aws_phone_number | prettyPhoneNumber}}</div>
        <div class="vsm-info-sub">'def [value] [vehicle name]' - for example 'def 13.2 123' (max 50)</div>
        <div class="vsm-info-sub">'fuel [value] [vehicle name]' - for example 'fuel 45 123' (max 500)</div>
        <div class="vsm-info-sub">'odo [value] [vehicle name]' - for example 'odo 12543 123' (max 1,500,000)</div>
        <div class="vsm-info-sub">parentheses format also supported, e.g. 'def(13.2) 123'</div>
        <div class="vsm-info-sub">commands are case-insensitive</div>
      </span>
    </b-modal>

    <b-modal title="Add Record" size="md" v-model="show_add_vehicle_info_modal" ok-only>
      <div>
        Vehicle Name: <b-form-input v-model="add_vehicle_info_model.name" required/>
        Base: <b-form-input v-model="add_vehicle_info_model.start_base" required/>
        Notes: <b-form-input v-model="add_vehicle_info_model.notes" required/>
        Next Scheduled Time: <datetime style="margin: 5px;border-radius: 5px;" format="MM/DD/YYYY H:i" :value="add_vehicle_info_model.next_scheduled_time" @input="add_vehicle_info_model.next_scheduled_time=$event"/>
      </div>
      <br/>
      <b-button v-if="!loading" variant="success" size="md" @click="add_vehicle_info_record()">Save</b-button>
    </b-modal>

    <div v-if="!loading && summary">
      <vsm-mobile-filter-chips
        :size="isMobile() ? 'sm' : 'md'"
        :total-format="isMobile() || standalone ? 'slash' : 'of'"
        :items-count="items.length"
        :total-count="summary.total_cnt"
        :total-selected="selected_vehicle_statuses.length === 0"
        :show-search="true"
        :search-filter="search_filter"
        @clear-filters="filter_by_status(null)"
        @update:searchFilter="search_filter = $event"
      >
        <span v-if="washed_enabled" class="vsm-chip vsm-chip-clickable ready" :class="{ 'vsm-chip-selected': selected_vehicle_statuses.includes('ready') }" @click="filter_by_status('ready')">Ready ({{ ready_cnt }})</span>
        <span class="vsm-chip vsm-chip-clickable cleaned" :class="{ 'vsm-chip-selected': selected_vehicle_statuses.includes('cleaned') }" @click="filter_by_status('cleaned')">Clean ({{ clean_cnt }})</span>
        <span v-if="washed_enabled" class="vsm-chip vsm-chip-clickable washed" :class="{ 'vsm-chip-selected': selected_vehicle_statuses.includes('washed') }" @click="filter_by_status('washed')">Washed ({{ washed_cnt }})</span>
        <span class="vsm-chip vsm-chip-clickable dirty" :class="{ 'vsm-chip-selected': selected_vehicle_statuses.includes('dirty') }" @click="filter_by_status('dirty')">Dirty ({{ dirty_cnt }})</span>
        <span class="vsm-chip vsm-chip-clickable out" :class="{ 'vsm-chip-selected': selected_vehicle_statuses.includes('out') }" @click="filter_by_status('out')">Out ({{ out_cnt }})</span>
        <span class="vsm-chip vsm-chip-clickable oos" :class="{ 'vsm-chip-selected': selected_vehicle_statuses.includes('oos') }" @click="filter_by_status('oos')">OOS ({{ oos_cnt }})</span>
        <template #extras>
          <span v-if="!isMobile()" class="vsm-filter-extras">
            <b-form-select v-if="base_options && base_options.length > 2" v-model="selected_base" :options="base_options" class="vsm-filter-select"/>
            <b-form-select v-if="vehicle_tag_options && vehicle_tag_options.length > 2" v-model="selected_vehicle_tag" :options="vehicle_tag_options" class="vsm-filter-select"/>
            <b-btn v-if="!standalone && summary.include_vehicle_info_only_enabled" size="sm" @click="show_add_vehicle_info_modal = !show_add_vehicle_info_modal" class="vsm-filter-add-btn">Add Row</b-btn>
          </span>
        </template>
        <template #actions>
          <vsm-status-row-actions v-if="!standalone" @reload="load_data()" />
        </template>
      </vsm-mobile-filter-chips>
    </div>

    <!-- floating status picker (ops_vsm pattern); anchored by the mixin -->
    <vsm-status-picker-pop
      v-if="editing_cell"
      ref="pickerPop"
      :options="status_options"
      :current="picker_item && picker_item[picker_field]"
      :chip-class="washChipClass"
      :style-obj="picker_style"
      aria-label="Set wash status"
      @select="setStatus(picker_item, picker_field, $event)"
      @esc="cancelEdit(true)"
    />

    <div v-if="summary">
      <!-- phones get cards instead of the table (mirrors the VSM Upcoming pages) -->
      <vsm-mobile-cards v-if="cardView" :items="items" :fields="cardFields" :datetime-format="summary.datetime_format">
        <template #controls="{ item }">
          <span v-if="item.future_booking">FUTURE</span>
          <b-form-select v-else-if="standalone && (summary.visible_update_fields && summary.visible_update_fields.indexOf('username') == -1)" class="vsm-up-chip-select" :class="item.vehicle_status ? 'vsm-up-cell-' + item.vehicle_status.toLowerCase() : null" v-model="item.vehicle_status" :options="status_options" @change="add_new_status_record({}, item)"/>
          <b-form-select v-else-if="!standalone" class="vsm-up-chip-select" :class="item.vehicle_status ? 'vsm-up-cell-' + item.vehicle_status.toLowerCase() : null" v-model="item.vehicle_status" :options="status_options" @change="update_vehicle_status({}, item)"/>
          <span v-else-if="item.vehicle_status" class="vsm-up-mini-chip" :class="'vsm-up-cell-' + item.vehicle_status.toLowerCase()">{{ item.vehicle_status }}</span>
        </template>
      <template #body="{ item }">
      <!-- the standalone/portal Update flow (row-details form on the table) -->
      <div v-if="summary && summary.visible_update_fields" class="vsm-cards-update">
        <b-button size="sm" variant="primary" class="vsm-cards-update-btn" @click="toggle_card_update(item)">{{ item._card_update ? 'Close' : 'Update' }}</b-button>
        <div v-if="item._card_update" class="vsm-cards-update-form">
          <label class="vsm-cards-update-field" v-if="standalone">Username: <b-form-input size="sm" style="width:110px" v-model="item.username"/></label>
          <label class="vsm-cards-update-field">Status: <b-form-select size="sm" class="vsm-cards-update-select" v-model="item.vehicle_status" :options="status_options"/></label>
          <label class="vsm-cards-update-field" v-if="summary.visible_update_fields.indexOf('odometer') != -1">Odometer: <b-form-input size="sm" style="width:90px" v-model="item.odometer"/></label>
          <label class="vsm-cards-update-field" v-if="summary.visible_update_fields.indexOf('fuel_level') != -1">Fuel: <b-form-input size="sm" style="width:70px" v-model="item.fuel_level"/></label>
          <label class="vsm-cards-update-field" v-if="summary.visible_update_fields.indexOf('def_level') != -1">DEF: <b-form-input size="sm" style="width:70px" v-model="item.def_level"/></label>
          <label class="vsm-cards-update-field" v-if="summary.visible_update_fields.indexOf('dump_date') != -1">Dump: <b-form-checkbox v-model="item.dump_date"/></label>
          <label class="vsm-cards-update-field" v-if="summary.visible_update_fields.indexOf('detailed_date') != -1">Detailed: <b-form-checkbox v-model="item.detailed_date"/></label>
          <label class="vsm-cards-update-field">Notes: <b-form-input size="sm" style="width:140px" v-model="item.notes"/></label>
          <b-button variant="success" size="sm" @click="add_new_status_record(card_update_row(item), item)">Save Record</b-button>
          <b-button variant="outline-danger" size="sm" @click="delete_status_record(card_update_row(item), item)">Delete Record</b-button>
        </div>
      </div>
      </template>
      </vsm-mobile-cards>
      <b-table v-else :busy="loading" :sticky-header="!standalone ? '70vh' : false" bordered :items="items" :fields="visibleFields" :tbody-tr-class="get_row_color" :table-class="['vsm-status-table', rowColorsOff ? 'vsm-rowcolors-off' : null]"
               :current-page="currentPage" :sort-compare="tableSortCompare" :style="'font-size:' + summary.font_size + 'px'" :per-page="perPage">

        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
          </div>
        </template>

        <template slot="notes" slot-scope="row">
          <div v-if="!standalone" class="d-flex align-items-center justify-content-center">
            <b-form-input :class="['vst-notes-input', { 'vsm-mobile-notes-input': isMobile(), 'vst-notes-input--saved': notes_saved === row.item.name }]" :style="isMobile() ? ('font-size:' + summary.font_size + 'px') : ('font-size:' + summary.font_size + 'px; min-width:200px')" v-model="row.item.notes" :disabled="updating" v-on:focus="markEditing(row.item); noteFocus(row.item)" v-on:blur="update_notes(row, row.item)"/>
          </div>
          <div class="vst-notes-text" v-else>{{row.item.notes}}</div>
        </template>
        
        <template slot="vehicle_status" slot-scope="row">
          <span v-if="row.item.future_booking">
            FUTURE
          </span>
          <span v-else>
            <b-form-select class="vsm-up-chip-select vsm-status-select" v-if="standalone && (summary.visible_update_fields && summary.visible_update_fields.indexOf('username') == -1)" :style="isMobile() ? null : 'font-size:' + summary.font_size + 'px'" :class="[{ 'vsm-mobile-select': isMobile() }, row.item.vehicle_status ? 'vsm-up-cell-' + row.item.vehicle_status.toLowerCase() : null]" v-model="row.item.vehicle_status" :options="status_options" v-on:focus="markEditing(row.item)" @change="add_new_status_record(row, row.item)"/>
            <button
              v-else-if="!standalone"
              type="button"
              class="vst-chip-btn"
              title="Update wash status"
              aria-haspopup="menu"
              :aria-expanded="editing_cell === cellKey(row.item, 'vehicle_status') ? 'true' : 'false'"
              :aria-label="'Change wash status for ' + row.item.name"
              :data-status-trigger="cellKey(row.item, 'vehicle_status')"
              @click="startEdit(row.item, 'vehicle_status', $event)"
            ><span class="vst-status-chip vst-status-chip--trigger" :class="washChipClass(row.item.vehicle_status)">{{ statusLabel(row.item.vehicle_status) || '-' }}<i class="fa fa-caret-down vst-status-caret" aria-hidden="true"></i></span></button>
            <span v-else-if="rowColorsOff && row.item.vehicle_status" class="vst-status-chip" :class="washChipClass(row.item.vehicle_status)">{{row.item.vehicle_status}}</span>
            <span v-else>{{row.item.vehicle_status}}</span>
          </span>
        </template>

        <template slot="name" slot-scope="row">
          <a class="vst-vehicle-link" target="_blank" :href="'/#/vehicle-track/' + tenant_id + '/' + row.item.name">{{row.item.name}}</a>
        </template>

        <template slot="current_mvmt_id" slot-scope="row">
          <router-link style="font-weight: bold;" :to="{ path: '/actuals?booking_id=' + row.item.current_mvmt_booking_hash }">{{ row.item.current_mvmt_id }}</router-link>
        </template>

        <template slot="last_fueled_time" slot-scope="row">
            {{row.item.last_fueled_time | formatDateTimeCustom(summary.datetime_format)}}
        </template>

        <template slot="last_gps_time" slot-scope="row">
          {{row.item.last_gps_time | formatDateTimeCustom(summary.datetime_format)}}
        </template>

        <template slot="processed_time" slot-scope="row">
          <span v-if="row.item.vehicle_info">
            {{row.item.processed_time | formatDateTimeCustom(summary.datetime_format)}}
          </span>
        </template>

        <template slot="last_dirty_time" slot-scope="row">
          <span v-if="row.item.vehicle_info">
            {{row.item.last_dirty_time | formatDateTimeCustom(summary.datetime_format)}}
          </span>
        </template>

        <template slot="last_oos_time" slot-scope="row">
          <span v-if="row.item.vehicle_info">
            {{row.item.last_oos_time | formatDateTimeCustom(summary.datetime_format)}}
          </span>
        </template>

        <template slot="dump_date" slot-scope="row">
            {{row.item.dump_date | formatDateTimeCustom(summary.datetime_format)}}
        </template>

        <template slot="detailed_date" slot-scope="row">
          {{row.item.detailed_date | formatDateTimeCustom(summary.datetime_format)}}
        </template>

        <template slot="last_returned_time" slot-scope="row">
          <span v-if="row.item.vehicle_info">
            {{row.item.last_returned_time | formatDateTimeCustom(summary.datetime_format)}}
          </span>
        </template>

        <template slot="last_cleaned_time" slot-scope="row">
          <span v-if="row.item.vehicle_info">
            {{row.item.last_cleaned_time | formatDateTimeCustom(summary.datetime_format)}}
          </span>
        </template>

        <template slot="last_cleaned_number" slot-scope="row">
          <span v-if="row.item.vehicle_info">
            {{row.item.last_cleaned_number}}
          </span>
        </template>

        <template slot="last_washed_time" slot-scope="row">
          <span v-if="row.item.vehicle_info" :style="row.item.last_washed_delta ? 'color:red' : ''">
              {{row.item.last_washed_time | formatDateTimeCustom(summary.datetime_format)}}
          </span>
        </template>

        <template slot="last_washed_number" slot-scope="row">
          <span v-if="row.item.vehicle_info">
            {{row.item.last_washed_number}}
          </span>
        </template>

        <template slot="next_mvmt_driver" slot-scope="row">
          {{row.item.next_mvmt_driver | trim(14)}}
          <template v-if="row.item.next_mvmt_list" v-for="next_mvmt in row.item.next_mvmt_list">
            <br/>{{next_mvmt.next_mvmt_driver | trim(14)}}
          </template>

        </template>

        <template slot="last_ready_time" slot-scope="row">
          <span v-if="row.item.vehicle_info">
            {{row.item.last_ready_time | formatDateTimeCustom(summary.datetime_format)}}
          </span>
        </template>

        <template slot="next_mvmt_start_base" slot-scope="row">
          {{row.item.next_mvmt_start_base}}&nbsp;
          <template v-if="row.item.next_mvmt_list" v-for="next_mvmt in row.item.next_mvmt_list">
            <br/>{{next_mvmt.next_mvmt_start_base}}&nbsp;
          </template>
        </template>

        <template slot="next_scheduled_time" slot-scope="row">
          <div v-if="!standalone">
            <datetime @click="row.toggleDetails();" style="margin: 5px;border-radius: 5px;" format="MM/DD/YYYY H:i" v-model="row.item.next_scheduled_time"  @input="update_vehicle_status(row, row.item)"/>
          </div>
          <div v-else>
            {{row.item.next_scheduled_time | formatDateTimeCustom(summary.datetime_format)}}
          </div>
        </template>
        
        <template slot="next_mvmt_driver_start" slot-scope="row">
          {{row.item.next_mvmt_driver_start | convert24HrDateTimeSmall(summary.datetime_format)}}
        </template>

        <template slot="next_mvmt_time" slot-scope="row">
          {{row.item.next_mvmt_time | convert24HrDateTimeSmall(summary.datetime_format)}}
          <template v-if="row.item.next_mvmt_list" v-for="next_mvmt in row.item.next_mvmt_list">
            <br/>{{next_mvmt.next_mvmt_time}}
          </template>
        </template>

        <template slot="next_mvmt_id" slot-scope="row">
          {{row.item.next_mvmt_id}}
          <template v-if="row.item.next_mvmt_list" v-for="next_mvmt in row.item.next_mvmt_list">
            <br/>{{next_mvmt.next_mvmt_id}}
          </template>
        </template>

        <template slot="next_mvmt_delta" slot-scope="row">
          <span v-if="row.item.next_mvmt_delta" :style="row.item.next_mvmt_delta.indexOf('d') != -1 ? '' : 'color: red'">
            {{row.item.next_mvmt_delta}}
          </span>
          <template v-if="row.item.next_mvmt_list" v-for="next_mvmt in row.item.next_mvmt_list">
            <br/>{{next_mvmt.next_mvmt_delta}}
          </template>
        </template>

        <template slot="next_mvmt_return_time" slot-scope="row">
          {{row.item.next_mvmt_return_time | formatDateTimeCustom(summary.datetime_format)}}
          <template v-if="row.item.next_mvmt_list" v-for="next_mvmt in row.item.next_mvmt_list">
            <br/>{{next_mvmt.next_mvmt_return_time | formatDateTimeCustom(summary.datetime_format)}}
          </template>
        </template>

        <template slot="update_status" slot-scope="row">
          <b-button size="lg" style="margin: 10px" @click="toggleRowEditing(row, row.item); updating = !updating">
            Update Status
          </b-button>
        </template>

        <template slot="delete" slot-scope="row">
          <div style="font-weight: bold; color: darkred; padding:3px" @click="delete_status_record(row, row.item)">X</div>
        </template>

        <template slot="details" slot-scope="row">
          <span v-if="row.item.future_booking">&nbsp;</span>
          <b-button v-else-if="standalone && summary.visible_update_fields && summary.visible_update_fields.indexOf('username') != -1" size="lg" style="margin: 10px" @click="toggleRowEditing(row, row.item); updating = !updating">
            Update
          </b-button>
          <button
            v-else-if="!standalone || summary.visible_update_fields"
            type="button"
            class="vst-expand-btn"
            :class="{ 'vst-expand-btn--open': row.detailsShowing }"
            aria-label="Show status history"
            :aria-expanded="row.detailsShowing ? 'true' : 'false'"
            @click="get_vehicle_info_history(row.item); toggleRowEditing(row, row.item);"
          ><i class="fa fa-chevron-right"></i></button>
        </template>

        <template slot="row-details" slot-scope="row">
          <b-card style="background-color: white; text-align: left;">

            <div v-if="summary.visible_update_fields">
              <b>Add New Status Record</b>
              <div class="row" style="font-weight: bold; margin: 5px">
                <div v-if="standalone" style="padding: 5px; margin: 5px" class="d-flex align-items-center justify-content-center">Username:&nbsp;&nbsp;<b-form-input style="width: 150px" v-model="row.item.username" v-on:focus="markEditing(row.item)"/></div>
                <div style="padding: 15px; margin: 5px" class="d-flex align-items-center justify-content-center">Status:&nbsp;&nbsp;<b-form-select style="width: 200px" v-model="row.item.vehicle_status" :options="status_options" v-on:focus="markEditing(row.item)"/></div>
                <div v-if="summary.visible_update_fields && summary.visible_update_fields.indexOf('odometer') != -1" style="padding: 15px; margin: 5px" class="d-flex align-items-center justify-content-center">Odometer:&nbsp;&nbsp;<b-form-input style="width: 150px" v-model="row.item.odometer" v-on:focus="markEditing(row.item)"/></div>
                <div v-if="summary.visible_update_fields && summary.visible_update_fields.indexOf('fuel_level') != -1" style="padding: 15px; margin: 5px" class="d-flex align-items-center justify-content-center">Fuel:&nbsp;&nbsp;<b-form-input style="width: 150px" v-model="row.item.fuel_level" v-on:focus="markEditing(row.item)"/></div>
                <div v-if="summary.visible_update_fields && summary.visible_update_fields.indexOf('def_level') != -1" style="padding: 15px; margin: 5px" class="d-flex align-items-center justify-content-center">DEF:&nbsp;&nbsp;<b-form-input style="width: 150px" v-model="row.item.def_level" v-on:focus="markEditing(row.item)"/></div>
                <div v-if="summary.visible_update_fields && summary.visible_update_fields.indexOf('dump_date') != -1" style="padding: 15px; margin: 5px" class="d-flex align-items-center justify-content-center">Dump:&nbsp;&nbsp;<b-form-checkbox size="lg" v-model="row.item.dump_date" v-on:focus="markEditing(row.item)"/></div>
                <div v-if="summary.visible_update_fields && summary.visible_update_fields.indexOf('detailed_date') != -1" style="padding: 15px; margin: 5px" class="d-flex align-items-center justify-content-center">Detailed:&nbsp;&nbsp;<b-form-checkbox size="lg" v-model="row.item.detailed_date" v-on:focus="markEditing(row.item)"/></div>
                <div style="padding: 15px; margin: 5px" class="d-flex align-items-center justify-content-center">Notes:&nbsp;&nbsp;<b-form-input style="width: 150px" v-model="row.item.notes" v-on:focus="markEditing(row.item)"/></div>
                <div style="padding: 15px; margin: 5px"><b-button variant="success" size="md" @click="add_new_status_record(row, row.item)">Save Record</b-button></div><br/>
                <div style="padding: 15px; margin: 5px"><b-button variant="success" size="md" @click="delete_status_record(row, row.item)">Delete Record</b-button></div><br/>
                <b-alert v-model="alert.show" :variant="alert.variant" dismissible fade>
                  {{alert.message}}
                </b-alert>
              </div>
              <hr/>
            </div>
            <!-- status change history: when/by plus the old -> new values that
                 changed in each update -->
            <vsm-status-history-list v-if="!standalone" :history="row.item.history" :datetime-format="summary.datetime_format"/>
          </b-card>
        </template>
      </b-table>
      <b-row v-if="!standalone && !cardView && items.length > 10">
        <b-col>
          <b-pagination
              v-model="currentPage"
              :total-rows="items.length"
              :per-page="perPage"
              align="center"
          >
          </b-pagination>
        </b-col>
      </b-row>
    </div>
    <div v-else>
      <div class="text-center text-danger my-2">
        <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
      </div>
    </div>
  </div>
</template>

<script>
import {delete_vehicle_status, get_vehicle_info_history, update_vehicle_status, update_vehicle_status_anonymous} from '../networking/apis'
import {get_tbn_vehicle_info, tbn_update_vehicle_status} from "../networking/tbn_apis";
import {mapState} from "vuex";
import moment from 'moment';
import datetime from 'vuejs-datetimepicker';
import { VSM_MOBILE_DEFAULT_FIELD_KEYS } from '../utils/vsm_fields'
import { snapshotStatusRecordFields, buildStatusRecordPayload, preserveActiveEdits } from '../utils/vsm_status_record_diff'
import VsmStandaloneUrlCopyButton from './VsmStandaloneUrlCopyButton.vue'
import VsmStatusRowActions from './VsmStatusRowActions.vue'
import VsmReloadButton from './VsmReloadButton.vue'
import VsmPrintButton from './VsmPrintButton.vue'
import VsmMobileFilterChips from './vsm_mobile_filter_chips.vue'
import VsmMobileCards from './vsm_mobile_cards.vue'
import VsmStatusPickerPop from './VsmStatusPickerPop.vue'
import VsmStatusHistoryList from './VsmStatusHistoryList.vue'
import { downloadVsmCsv } from '@/utils/vsm_csv'

import vsmMobileView from '@/mixins/vsm_mobile_view'
import vsmOpsTable from '@/mixins/vsm_ops_table'

export default {
  mixins: [vsmMobileView, vsmOpsTable],
  name: 'vehicle_status_tbn',
  components: {
     datetime,
     VsmStandaloneUrlCopyButton,
     VsmStatusRowActions,
     VsmReloadButton,
     VsmPrintButton,
     VsmMobileFilterChips,
     VsmMobileCards,
     VsmStatusPickerPop,
     VsmStatusHistoryList,
  },
  metaInfo: {
    title: "Fleet Integrations",
    titleTemplate: "Fleet Integrations",
  },
  data() {
    return {
      search_filter: '',
      tenant_id: null,
      updating: false,
      standalone: false,
      mode: '',
      return_mode: false,
      driver_mode: false,
      cleaning_mode: false,
      maintenance_mode: false,
      washed_enabled: false,
      view_more_info: false,
      update_status_modal: false,
      show_add_vehicle_info_modal: false,
      add_vehicle_info_model: {'manual_record': true},
      columnSelection: false,
      reloading: false,
      reload_mode: 0,
      timer: null,
      items: [],
      items_full: [],
      // fields the "Add New Status Record" panel can edit; used to diff
      // against a per-vehicle load-time snapshot so saving only sends what
      // the user actually changed, instead of the whole (possibly stale) row
      statusRecordFields: ['vehicle_status', 'odometer', 'fuel_level', 'def_level', 'dump_date', 'detailed_date', 'notes'],
      status_record_baselines: {},
      // names of rows the user is actively editing (notes focused, or the
      // "Add New Status Record" panel expanded) - protects against
      // load_data()/reload_data() replacing items mid-edit and wiping out
      // what the user is typing before they get a chance to save it
      active_edit_names: {},
      summary: null,
      loading: true,
      selected_vehicle_statuses: [],
      selected_base: '',
      selected_vehicle_tag: '',
      base_options: [],
      vehicle_tag_options: [],
      totalRows: 1,
      currentPage: 1,
      perPage: 300,
      pages: [],
      alert: {
        show: false,
        message: '',
        variant: ''
      },
      status_options: [
        { value: '', text: 'ALL' },
        { value: 'READY', text: 'READY' },
        { value: 'CLEANED', text: 'CLEANED' },
        { value: 'WASHED', text: 'WASHED' },
        { value: 'DIRTY', text: 'DIRTY' },
        { value: 'OUT', text: 'OUT' },
        { value: 'OOS', text: 'OOS' },
      ],
      reload_options: [
        { value: 0, text: 'OFF' },
        { value: 30000, text: '30s' },
        { value: 60000, text: '1m' },
        { value: 300000, text: '5m' },
      ],
      fields: [
        { key: 'delete', label: '', sortable: false, visible: true, thStyle: { width: "30px" }},
        { key: 'details', label: '', sortable: false, visible: true, thStyle: { width: "30px" }},
        { key: 'name', label: 'Vehicle', sortable: true, visible: true, thStyle: { width: "100px" }},
        { key: 'vehicle_status', label:'Status', sortable: true, visible: true, thStyle: { width: "150px" }},
        { key: 'vehicle_tags', label:'Tags', sortable: true, visible: true},
        { key: 'current_mvmt_id', label: 'Current ID', sortable: true, visible: true, thStyle: { width: "120px" }},
        { key: 'current_mvmt_driver', label: 'Driver', sortable: true, visible: true, thStyle: { width: "160px" }},
        { key: 'current_start_base', label: 'Current Start', sortable: true, visible: true},
        { key: 'current_finish_base', label: 'Current Finish', sortable: true, visible: true},
        { key: 'current_mvmt_return_time', tdClass: 'vsm-dt-cell', label: 'Current Return', sortable: true, visible: true, thStyle: { width: "140px" }},
        { key: 'last_fueled_time', tdClass: 'vsm-dt-cell', label:'Last Fueled', sortable: true, visible: true, thStyle: { width: "160px" }},
        { key: 'last_dirty_time', tdClass: 'vsm-dt-cell', label:'Dirty Time', sortable: true, visible: true, thStyle: { width: "140px" }},
        { key: 'last_cleaned_time', tdClass: 'vsm-dt-cell', label:'Cleaned Time', sortable: true, visible: true, thStyle: { width: "140px" }},
        { key: 'last_cleaned_number', label:'Cleaned #', sortable: true, visible: true},
        { key: 'last_washed_time', tdClass: 'vsm-dt-cell', label:'Washed Time', sortable: true, visible: true, thStyle: { width: "140px" }},
        { key: 'last_washed_number', label:'Washed #', sortable: true, visible: true},
        { key: 'last_ready_time', tdClass: 'vsm-dt-cell', label:'Ready Time', sortable: true, visible: true, thStyle: { width: "140px" }},
        { key: 'last_returned_time', tdClass: 'vsm-dt-cell', label: 'Last Returned Time', sortable: true, visible: true, thStyle: { width: "160px" }},
        { key: 'dump_date', label:'Dump Date', visible: false, sortable: true, thStyle: { width: "140px" }},
        { key: 'detailed_date', label:'Detailed Date', visible: false, sortable: true},
        { key: 'next_mvmt_start_base', label: 'Next Start', sortable: true, visible: true, thStyle: { width: "150px" } },
        { key: 'next_mvmt_route_name', label: 'Next Route Name', sortable: true, visible: true, thStyle: { width: "180px" }},
        { key: 'next_mvmt_driver', label: 'Next Driver', sortable: true, visible: true, thStyle: { width: "150px" }},
        { key: 'next_mvmt_driver_start', tdClass: 'vsm-dt-cell', label: 'Next Driver Start', sortable: true, visible: true, thStyle: { width: "150px" }},
        { key: 'next_mvmt_id', label: 'Next Booking ID', sortable: true, visible: true, thStyle: { width: "150px" }},
        { key: 'next_mvmt_booking_category', label: 'Booking Category', sortable: true, visible: true, thStyle: { width: "150px" }},
        { key: 'next_scheduled_time', tdClass: 'vsm-dt-cell', label:'Scheduled Time', sortable: true, visible: true, thStyle: { width: "180px" }},
        { key: 'next_mvmt_time', tdClass: 'vsm-dt-cell', label:'Next Departure', sortable: true, visible: true, thStyle: { width: "160px" }},
        { key: 'next_mvmt_delta', label:'Departs In', sortable: true, visible: true, thStyle: { width: "150px" }},
        { key: 'next_mvmt_return_time', tdClass: 'vsm-dt-cell', label:'Next Return', sortable: true, visible: true, thStyle: { width: "140px" }},
        { key: 'odometer', label:'Odometer', visible: false, sortable: true},
        { key: 'fuel_level', label:'Fuel', visible: false, sortable: true},
        { key: 'def_level', label:'DEF', visible: false, sortable: true},
        { key: 'fleetio_status', label:'Fleetio Status', sortable: true, thStyle: { width: "160px" }},
        { key: 'rta_status', label:'RTA Status', sortable: true, thStyle: { width: "160px" }},
        { key: 'rta_work_order_status', label:'RTA WO Status', sortable: true, thStyle: { width: "180px" }},
        { key: 'notes', label: 'Notes', sortable: true, visible: true, thStyle: { width: "200px" }},
        { key: 'update_status', label: '', sortable: false, visible: true, thStyle: { width: "200px" }},
      ],
      history_fields: [
        { key: 'processed_time', tdClass: 'vsm-dt-cell', label:'Last Updated', visible: true, sortable: true},
        { key: 'updated_by', label:'Updated By', visible: true, sortable: true},
        { key: 'vehicle_status', label:'Status', visible: true, sortable: true},
        { key: 'last_fueled_time', tdClass: 'vsm-dt-cell', label:'Last Fueled', sortable: true, visible: true, thStyle: { width: "140px" }},
        { key: 'last_cleaned_time', tdClass: 'vsm-dt-cell', label:'Cleaned Time', visible: true, sortable: true},
        { key: 'last_cleaned_number', label:'Cleaned #', visible: true, sortable: true},
        { key: 'last_washed_time', tdClass: 'vsm-dt-cell', label:'Washed Time', visible: true, sortable: true},
        { key: 'last_washed_number', label:'Washed #', visible: true, sortable: true},
        { key: 'last_ready_time', tdClass: 'vsm-dt-cell', label:'Ready Time', visible: true, sortable: true},
        { key: 'last_returned_time', tdClass: 'vsm-dt-cell', label: 'Returned Time', sortable: true, visible: true},
        { key: 'next_scheduled_time', tdClass: 'vsm-dt-cell', label:'Scheduled Time', sortable: true, visible: true, thStyle: { width: "180px" }},
        { key: 'odometer', label:'Odometer', visible: false, sortable: true},
        { key: 'fuel_level', label:'Fuel', visible: false, sortable: true},
        { key: 'def_level', label:'DEF', visible: false, sortable: true},
        { key: 'notes', label: 'Notes', visible: true, sortable: true},
      ]
    }
  },
  async created() {
    this.fields_all = this.fields
    await this.check_features()
  },
  mounted() {
    this.stopTimer()
  },
  beforeDestroy() {
    try { this.flushActiveEdits() } catch(e) { /* best-effort save on teardown */ }
    clearTimeout(this.visibilityReloadTimeout)
    this.stopTimer()
  },
  methods: {
    startTimer() {
      this.stopTimer()
      if (this.reload_mode > 0) {
        this.timer = setInterval(() => this.reload_data(), this.reload_mode)
      }
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    async resetTimerAndReload() {
      this.stopTimer()
      await this.reload_data()
      this.startTimer()
    },

    printPage() {
      window.print();
    },
    isMobile() {
      return this.mobileView
    },
    isTablet() {
      if(/iPad/i.test(navigator.userAgent)) {
        return true
      } else {
        return false
      }
    },
    showAlert(msg, variant, autoclose) {
      this.alert.show = true
      this.alert.message = msg
      this.alert.variant = variant
      autoclose
          ? setTimeout(() => this.closeAlert(), autoclose)
          : null
    },
    closeAlert() {
      this.alert.show = false
      this.alert.message = ''
      this.alert.variant = ''
    },
    tableSortCompare(aRow, bRow, key) {
      if (key == 'next_mvmt_delta') {
        return aRow['next_mvmt_ms'] - bRow['next_mvmt_ms']
      }
      return false;
    },
    async update_fueled_time(row, item, last_fueled_time) {
      if (this.reloading)
        return;

      this.updating = true
      try {

        let json = {
          'name': item.name,
          'last_fueled_time':  last_fueled_time,
        }

        const {value: { data = [] } } = await tbn_update_vehicle_status(this.tenant_id, json)

      } catch(e) {
        this.$emit('showAlert', 'error updating data, try logging in again or contact support if needed', 'danger', 5000)
      }

      this.updating = false
    },

    async check_features() {

      this.mode = ''
      this.return_mode = false
      this.driver_mode = false
      this.cleaning_mode = false
      this.maintenance_mode = false

      if (this.$route.path.indexOf('/vsm-tbn/') == -1) {
        this.standalone = false
        this.tenant_id = this.user.tenant
      }
      else if (this.$route.path.indexOf('/vsm-tbn/reload/') != -1) {
        this.standalone = true
        let filter = this.$route.path.split('/vsm-tbn/reload/')[1]

        if (filter.indexOf('/') != -1) {
          this.tenant_id = filter.split("/")[0];
          this.selected_base = filter.split("/")[1];
        } else {
          this.tenant_id = filter;
        }

        this.reload_mode = 60000
      }
      else if (this.$route.path.indexOf('/vsm-tbn/reloadreturn/') != -1) {
        this.standalone = true
        this.return_mode = true
        this.mode = 'return'
        let filter = this.$route.path.split('/vsm-tbn/reloadreturn/')[1]

        if (filter.indexOf('/') != -1) {
          this.tenant_id = filter.split("/")[0];
          this.selected_base = filter.split("/")[1];
        } else {
          this.tenant_id = filter;
        }

        this.reload_mode = 60000
      }
      else if (this.$route.path.indexOf('/vsm-tbn/driver/') != -1) {
        this.standalone = true
        this.driver_mode = true
        let filter = this.$route.path.split('/vsm-tbn/driver/')[1]

        if (filter.indexOf('/') != -1) {
          this.tenant_id = filter.split("/")[0];
          this.selected_base = filter.split("/")[1];
        } else {
          this.tenant_id = filter;
        }
        this.reload_mode = 60000
      }
      else if (this.$route.path.indexOf('/vsm-tbn/cleaning/') != -1) {
        this.standalone = true
        this.cleaning_mode = true
        let filter = this.$route.path.split('/vsm-tbn/cleaning/')[1]

        if (filter.indexOf('/') != -1) {
          this.tenant_id = filter.split("/")[0];
          this.selected_base = filter.split("/")[1];
        } else {
          this.tenant_id = filter;
        }
        this.reload_mode = 60000
      }
      else if (this.$route.path.indexOf('/vsm-tbn/maintenance/') != -1) {
        this.standalone = true
        this.maintenance_mode = true
        let filter = this.$route.path.split('/vsm-tbn/maintenance/')[1]

        if (filter.indexOf('/') != -1) {
          this.tenant_id = filter.split("/")[0];
          this.selected_base = filter.split("/")[1];
        } else {
          this.tenant_id = filter;
        }
        this.reload_mode = 60000
      }

      this.fields = this.fields_all
      if (this.standalone)
        this.fields = this.fields.filter(item => (item.key != 'details'))
      else
        this.fields = this.fields.filter(item => (item.key != 'update_status'))

      if (this.isMobile())
        this.fields = this.fields.filter(item => (item.key == 'name' || item.key == 'vehicle_status' || item.key == 'next_mvmt_delta'))

      if (this.isTablet())
        this.fields = this.fields.filter(item => (item.key != 'current_mvmt_return_time' && item.key != 'last_ready_time' && item.key != 'next_mvmt_start_base' && item.key != 'next_mvmt_id'))

      this.load_data()
    },
    // picker save hook (vsm_ops_table mixin): optimistic set, then the same
    // endpoint path the old dropdown used
    applyStatusChange(item, field, value) {
      this.$set(item, field, value)
      this.update_vehicle_status({}, item)
    },
    // auto-refresh hook (vsm_ops_table mixin)
    downloadCsv() {
      downloadVsmCsv(this.visibleFields, this.items, 'vsm-' + moment().format('MM-DD-YYYY') + '.csv')
    },
    // card-mode Update flow: mirrors the table's row-details add-record form
    toggle_card_update(item) {
      this.$set(item, '_card_update', !item._card_update)
    },
    // the save/delete handlers expect a b-table row; this stub closes the
    // card section the way toggleDetails closes the desktop expansion
    card_update_row(item) {
      return { detailsShowing: true, toggleDetails: () => this.$set(item, '_card_update', false) }
    },
    opsSoftReload() {
      this.reload_data()
    },
    async update_vehicle_status(row, item) {
      this.updating = true
      try {

        let json = {
          'name': item.name,
          'vehicle_status':  item.vehicle_status,
          'next_scheduled_time':  item.next_scheduled_time,
        }

        const {value: { data = [] } } = await tbn_update_vehicle_status(this.tenant_id, json)

        //update any caclulated fields that might have changed during the save
        if (data.vehicle_status)
          item.vehicle_status = data.vehicle_status
        if (data.dump_date)
          item.dump_date = data.dump_date
        if (data.detailed_date)
          item.detailed_date = data.detailed_date

        if (row.detailsShowing)
          row.toggleDetails();

        // if (item.next_scheduled_time) {
        //   console.log("item.next_scheduled_time1:" + item.next_scheduled_time)
        //   item.next_scheduled_time = moment(item.next_scheduled_time, 'MM/DD/YYYY H:mm').format('MM/DD/YYYY h:mm a')
        //   console.log("item.next_scheduled_time2:" + item.next_scheduled_time)
        // }

      } catch(e) {
        this.$emit('showAlert', 'error updating data, try logging in again or contact support if needed', 'danger', 5000)
      }

      this.clearEditing(item)
      this.updating = false
    },
    async add_vehicle_info_record() {
      this.updating = true
      try {
        await update_vehicle_status(this.tenant_id, this.add_vehicle_info_model)
        this.add_vehicle_info_model = {'manual_record': true}
        this.show_add_vehicle_info_modal = false
      } catch(e) {
        this.$emit('showAlert', 'error updating data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.updating = false
      //this.reload_data()
    },
    async delete_status_record(row, selected_item) {
      this.updating = true
      try {
        if(confirm("Do you really want to delete status record for " + selected_item.name + "?")) {
          const {value: {data = []}} = await delete_vehicle_status(this.tenant_id, selected_item)
          this.items = this.items.filter(item => item.name != selected_item.name)
        }
      } catch(e) {
        this.$emit('showAlert', 'error updating data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.clearEditing(selected_item)
      this.updating = false
    },
    async add_new_status_record(row, item) {
      this.updating = true
      try {
        // only send the fields this user actually changed since the row was
        // loaded, so a stale panel can't clobber another user's more recent
        // edit to a field this user never touched
        const payload = buildStatusRecordPayload(this.status_record_baselines[item.name], item, this.statusRecordFields)

        if (this.standalone) {
          try {
            if (this.summary.visible_update_fields.indexOf('username') == -1)
              item.username = 'anonymous'
            payload.username = item.username

            const {value: {data = []}} = await update_vehicle_status_anonymous(this.tenant_id, payload)
            if (data.vehicle_status)
              item.vehicle_status = data.vehicle_status
            if (data.dump_date)
              item.dump_date = data.dump_date
            if (data.detailed_date)
              item.detailed_date = data.detailed_date
            if (data.wrapped_date)
              item.wrapped_date = data.wrapped_date

          } catch(e){
            this.showAlert("unable to save new status record, invalid username", 'danger', 5000)
            return;
          }
        } else {
          const {value: {data = []}} = await update_vehicle_status(this.tenant_id, payload)
          if (data.vehicle_status)
            item.vehicle_status = data.vehicle_status
          if (data.dump_date)
            item.dump_date = data.dump_date
          if (data.detailed_date)
            item.detailed_date = data.detailed_date
          if (data.wrapped_date)
            item.wrapped_date = data.wrapped_date
          if (data.last_cleaned_time)
            item.last_cleaned_time = data.last_cleaned_time
          if (data.last_washed_time)
            item.last_washed_time = data.last_washed_time
          if (data.last_bathroom_cleaned_time)
            item.last_bathroom_cleaned_time = data.last_bathroom_cleaned_time
          if (data.last_def_filled_time)
            item.last_def_filled_time = data.last_def_filled_time
          if (data.last_diesel_filled_time)
            item.last_diesel_filled_time = data.last_diesel_filled_time
        }

        this.status_record_baselines[item.name] = snapshotStatusRecordFields(item, this.statusRecordFields)

        if (this.summary.visible_update_fields.indexOf('username') != -1)
          row.toggleDetails();

      } catch(e) {
        this.$emit('showAlert', 'error updating data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.clearEditing(item)
      this.updating = false
    },
    async load_data() {
      this.loading = true
      try {
        var results = await get_tbn_vehicle_info(this.tenant_id, this.selected_base, this.selected_vehicle_tag, this.mode)
        const {value: { data = [] } } = results

        this.summary = data.summary
        moment.tz.setDefault(this.summary.timezone)
        data.results = preserveActiveEdits(this.items_full, data.results, this.active_edit_names, this.statusRecordFields)
        this.items = data.results
        this.items_full = data.results
        this.refreshStatusRecordBaselines()
        this.base_options = data.summary.bases
        this.vehicle_tag_options = data.summary.vehicle_tags
        this.washed_enabled = this.summary.washed_enabled
        if (!this.washed_enabled)
          this.status_options = this.status_options.filter(item => (item.value != 'READY' && item.value != 'WASHED'))

        this.filter_items()

        this.stopTimer()
        if (this.reload_mode > 0) {
          this.startTimer()
        }
      } catch(e) {
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }

      this.loading = false
    },
    async reload_data() {
      try {
        if (this.updating)
          return;

        if (this.$pageVisibility && this.$pageVisibility.hidden)
          return;

        if (this.reloading)
          return;

        this.reloading = true

        var results = await get_tbn_vehicle_info(this.tenant_id, this.selected_base, this.selected_vehicle_tag, this.mode)
        const {value: { data = [] } } = results

        this.summary = data.summary
        moment.tz.setDefault(this.summary.timezone)
        data.results = preserveActiveEdits(this.items_full, data.results, this.active_edit_names, this.statusRecordFields)
        // merge-by-name so open history rows and a focused notes input
        // survive the background refresh (vsm_ops_table)
        this.items_full = this.vsmMergeRows(this.items_full, data.results)
        this.items = this.items_full
        this.refreshStatusRecordBaselines()
        this.base_options = data.summary.bases
        this.filter_items()
      } catch(e) {
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }

      this.reloading = false
    },
    // snapshots each row's editable "Add New Status Record" fields as
    // loaded from the server, so add_new_status_record() can later tell
    // which fields this user actually changed vs. what just came from the
    // last load/reload
    refreshStatusRecordBaselines() {
      const baselines = {}
      this.items_full.forEach(item => {
        baselines[item.name] = snapshotStatusRecordFields(item, this.statusRecordFields)
      })
      this.status_record_baselines = baselines
    },
    markEditing(item) {
      if (item) this.active_edit_names[item.name] = true
    },
    clearEditing(item) {
      if (item) delete this.active_edit_names[item.name]
    },
    // called when the component is torn down (e.g. navigating to a different
    // VSM page) so any edit the user hadn't clicked/tabbed away from yet
    // still gets saved instead of silently discarded
    flushActiveEdits() {
      Object.keys(this.active_edit_names).forEach(name => {
        const item = (this.items || []).find(i => i.name === name)
        if (item) this.add_new_status_record({ toggleDetails: () => {} }, item)
      })
    },
    // wraps row.toggleDetails() so expanding the "Add New Status Record"
    // panel flags the row as being edited (protecting it from a background
    // reload wiping in-progress field values) and collapsing it clears that
    // flag again
    toggleRowEditing(row, item) {
      if (row.detailsShowing) {
        this.clearEditing(item)
      } else {
        this.markEditing(item)
      }
      row.toggleDetails()
    },
    async update_notes(row, item) {
      this.updating = true
      try {
        let json = {
          'name': item.name,
          'notes': item.notes,
        }

        const {value: { data = [] } } = await tbn_update_vehicle_status(this.tenant_id, json)
        this.flashNotesSaved(item.name)
        if (row.detailsShowing)
          row.toggleDetails();
      } catch(e) {
        if (this.notes_editing === item.name)
          this.notes_editing = ''
        this.$emit('showAlert', 'error updating data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.clearEditing(item)
      this.updating = false
    },
    async get_vehicle_info_history(selected_item) {
      const {value: { data = [] } } = await get_vehicle_info_history(this.tenant_id, selected_item.name)
      var history_item = this.items_full.filter(item => item.name == selected_item.name)[0]
      history_item.history = data
      this.$forceUpdate()
    },
    filter_by_status(status) {
      if (status == null) {
        this.selected_vehicle_statuses = []
      } else if (this.selected_vehicle_statuses.includes(status)) {
        this.selected_vehicle_statuses = this.selected_vehicle_statuses.filter(s => s !== status)
      } else {
        this.selected_vehicle_statuses = [...this.selected_vehicle_statuses, status]
      }
      this.filter_items()
    },
    filter_items(status) {
      this.items = this.items_full
      if (this.selected_vehicle_statuses.length > 0) {
        const upper = this.selected_vehicle_statuses.map(s => s.toUpperCase())
        this.items = this.items.filter(item => upper.includes(item.vehicle_status))
      }
      if (this.selected_vehicle_tag) {
        this.items = this.items.filter(item => this.contains(item.vehicle_tags,this.selected_vehicle_tag))
      }
      if (status) {
        this.items = this.items.filter(item => item.vehicle_status == status)
      }
      if (this.search_filter != '') {
        this.items = this.items.filter(item => (this.contains(item.start_bases, this.search_filter) || this.contains(item.next_mvmt_driver, this.search_filter) || this.contains(item.name, this.search_filter) || this.contains(item.notes, this.search_filter)))
      }
    },
    get_filter_counts(status) {
      let items_tmp = this.items_full
      if (status) {
        items_tmp = items_tmp.filter(item => item.vehicle_status == status)
      }
      if (this.search_filter != '') {
        items_tmp = items_tmp.filter(item => (this.contains(item.start_bases, this.search_filter) || this.contains(item.name, this.search_filter)))
      }
      return items_tmp.length
    },

    contains(val1, val2) {
      if (val1 != null) {
        return val1.toLowerCase().indexOf(val2.toLowerCase()) != -1;
      }
      return false;
    },
    get_row_color(item) {
      if (item && !this.driver_mode) {
        if (item.vehicle_status)
          return item.vehicle_status.toLowerCase()
        else
          return "unknown";
      }
    },
    get_history_row_color(item) {
      return "unknown";
    },
},
  computed: {
    rowColorsOff() {
      const v = this.summary && this.summary.row_colors_disabled
      return v === true || String(v).toLowerCase() === 'true'
    },
    ...mapState({
      user: state => state.profile.user
    }),
    standaloneId() {
      return (this.summary && this.summary.vsm_standalone_uuid) || this.tenant_id
    },
    cardFields() {
      // the portal's visible column set (not the curated mobile subset):
      // tenant's visible_fields config when present, else the default table
      // catalog — so the cards carry everything the desktop table shows
      if (this.summary && this.summary.visible_fields) {
        return this.summary.visible_fields.split(',')
          .map(f => f.trim())
          .map(key => this.fields_all.filter(field => field.key === key)[0])
          .filter(Boolean)
      }
      return (this.fields_all || []).filter(field => field.visible)
    },
    visibleFields() {
      var custom_fields = null;

      if (this.summary && this.isMobile() && this.summary.visible_mobile_fields)
        custom_fields = this.summary.visible_mobile_fields;
      else if (this.summary && this.driver_mode && this.summary.visible_driver_fields)
        custom_fields = this.summary.visible_driver_fields;
      else if (this.summary && this.cleaning_mode && this.summary.visible_cleaning_fields)
        custom_fields = this.summary.visible_cleaning_fields;
      else if (this.summary && this.maintenance_mode && this.summary.visible_maintenance_fields)
        custom_fields = this.summary.visible_maintenance_fields;
      else if (this.summary && this.standalone && this.summary.visible_standalone_fields)
        custom_fields = this.summary.visible_standalone_fields;
      else if (this.summary && this.summary.visible_fields)
        custom_fields = this.summary.visible_fields;

      if (this.standalone && !this.summary.visible_update_fields) {
        this.fields_all = this.fields_all.filter(item => (item.key != 'details'))
        this.fields = this.fields.filter(item => (item.key != 'details'))
      }

      if (custom_fields != null) {
        let new_fields = []
        for (let visible_field of custom_fields.split(",").map(f => f.trim())) {
          let found_field = this.fields_all.filter(field => visible_field == field.key)
          if (found_field.length > 0) {
            new_fields.push(found_field[0]);
          }
        }
        return new_fields;
      } else {
        // no field config resolved for this surface: phones used to fall
        // through to the full catalog below. use the curated mobile set.
        if (this.isMobile()) {
          let mobile_fields = []
          for (let key of VSM_MOBILE_DEFAULT_FIELD_KEYS) {
            let found_field = this.fields_all.filter(field => key == field.key)
            if (found_field.length > 0) mobile_fields.push(found_field[0])
          }
          if (mobile_fields.length > 1) return mobile_fields
        }
        return this.fields.filter(field => field.visible)
      }
    },
    visibleHistoryFields() {
      var custom_fields = null;

      if (this.summary && this.summary.visible_history_fields)
        custom_fields = this.summary.visible_history_fields;

      if (custom_fields != null) {
        let new_fields = []
        for (let visible_field of custom_fields.split(",").map(f => f.trim())) {
          let found_field = this.history_fields.filter(field => visible_field == field.key)
          if (found_field.length > 0) {
            new_fields.push(found_field[0]);
          }
        }
        return new_fields;
      } else
        return this.history_fields
    },
    ready_cnt() {
      return this.get_filter_counts('READY')
    },
    clean_cnt() {
      return this.get_filter_counts('CLEANED')
    },
    washed_cnt() {
      return this.get_filter_counts('WASHED')
    },
    dirty_cnt() {
      return this.get_filter_counts('DIRTY')
    },
    out_cnt() {
      return this.get_filter_counts('OUT')
    },
    oos_cnt() {
      return this.get_filter_counts('OOS')
    },
  },
  watch: {
    // crossing the phone breakpoint re-derives the field set (check_features
    // trims fields under isMobile(); without this a widened window keeps the
    // 3-column phone set)
    mobileView() {
      this.check_features()
    },
    '$route'() {
      this.stopTimer()
      this.check_features()
    },
    user: {
      deep: true,
      handler() {
        this.check_features()
      }
    },
    selected_base(val) {
      this.load_data()
    },
    search_filter(val) {
      this.filter_items()
    },
    selected_vehicle_tag(val) {
      this.filter_items()
    },
    reload_mode(val) {
      this.stopTimer()
      if (val > 0)
        this.startTimer()
    },
    // debounced: some environments (VMs, remote desktop, etc.) fire
    // document visibilitychange rapidly/spuriously rather than on a real
    // tab-switch, which was causing a reload storm that stomped on
    // whatever the user was actively typing before their edit could ever
    // register. Only reload once visibility has actually settled.
    '$pageVisibility.hidden'(newVal, oldVal) {
      clearTimeout(this.visibilityReloadTimeout)
      if (oldVal && !newVal) {
        this.visibilityReloadTimeout = setTimeout(() => {
          if (!this.$pageVisibility.hidden) this.resetTimerAndReload()
        }, 1000)
      }
    }
  }
}
</script>

<style scoped>
.total { border: 1px solid grey; border-radius: 5px; padding: 2px; margin:2px}

.cleaned { background-color: hsla(190,70%,50%,0.4); border: 1px solid grey; border-radius: 5px; padding: 2px; margin:2px}
.washed { background-color: hsla(160,30%,40%,0.4); border: 1px solid grey; border-radius: 5px; padding: 2px; margin:2px}
.ready { background-color: hsla(120,100%,75%,0.3); border: 1px solid grey; border-radius: 5px; padding: 2px; margin:2px}
.oos { background-color: hsla(357,78%,65%,0.4); border: 1px solid grey; border-radius: 5px; padding: 2px; margin:2px}
.dirty { background-color: hsla(25,28%,23%,0.4); border: 1px solid grey; border-radius: 5px; padding: 2px; margin:2px}
.out { background-color: hsla(290,60%,70%,0.3); border: 1px solid grey; border-radius: 5px; padding: 2px; margin:2px}

.unknown td { background-color: white !important;}
.cleaned td { background-color: hsla(190,70%,50%,0.4); border: 1px solid black !important;}
.washed td { background-color: hsla(160,30%,40%,0.4); border: 1px solid black !important; }
.ready td { background-color: hsla(120,100%,75%,0.3); border: 1px solid black !important; }
.oos td { background-color: hsla(357,78%,65%,0.4); border: 1px solid black !important; }
.dirty td { background-color: hsla(25,28%,23%,0.4); border: 1px solid black !important; }
.out td { background-color: hsla(290,60%,70%,0.3); border: 1px solid black !important; }

div.row:nth-child(even) {
  background-color: #f2f2f2;
}

tr:nth-child(even) {
  background-color: #f2f2f2;
}

.header {
  font-weight: bold;
}

div.col {
  border: 1px solid grey;
}

.table td {
  padding: .1rem !important;
}

.mvmt-table td {
  vertical-align: middle !important;
}

div.wrap {
  border: 1px solid grey;
  flex-wrap: wrap;
  display: flex;
  inline-size: 150px;
  /* overflow-wrap: break-word; */
}

.stats { border-radius: 5px; margin:2px}
</style>

